// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { BootstrapLink } from '../../dsl/front';
import { AbstractModuleUi } from '../_abstract/ui';
import { AppTheme } from '../_contracts';
import { LineHeader, Tile, TileSize } from '../_components/ui';
import { getArticlesCount } from './Tile.helpers';
import { tileVersionTileSizeRegistry } from './Tile.config';
/**
 * Component capable of rendering the `TileModule's` contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
let TileModuleUi = class TileModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * Array of the related articles.
         */
        this.articles = [];
        this.headerLink = null;
        this.AppTheme = AppTheme;
    }
    /**
     * @inheritDoc
     */
    get articlesCount() {
        return getArticlesCount(this.module.size, this.module.version);
    }
    get lineHeaderTitleTag() {
        if (this.module.content.header) {
            return this.module.content.header.isH1 ? 'h1' : 'h2';
        }
        return 'h2';
    }
    /**
     * Determines whether there's a header text to render.
     */
    get hasHeaderText() {
        return typeof this.module.content.header !== 'undefined' &&
            typeof this.module.content.header.text === 'string' &&
            this.module.content.header.text.length > 0;
    }
    /**
     * Determines whether there's a header to be rendered.
     */
    get shouldShowHeader() {
        const { header = {} } = this.module.content;
        if (!this.module.content.showHeader) {
            return false;
        }
        const keys = ['site', 'text'];
        for (const key of keys) {
            const value = header[key];
            if (typeof value === 'string' && value.length > 0) {
                return true;
            }
        }
        return false;
    }
    /**
     * Configuration settings for the `<Tile>` component.
     */
    get tileConfig() {
        const size = this.tileSize;
        let showCategory = false;
        const { isPremium, showAuthorAvatar, showAuthorName, showArticleTags, showPublicationDate, showTitle, titleOnImage, titleVersion, gradientColor } = this.module.content;
        if ((size === TileSize.L || size === TileSize.XL) && this.module.content.showCategory) {
            showCategory = this.module.content.showCategory;
        }
        return {
            isPremium,
            showAuthorAvatar,
            showAuthorName,
            showCategory,
            showPublicationDate,
            showTitle,
            showArticleTags,
            titleOnImage,
            titleVersion,
            gradientColor,
            size
        };
    }
    /**
     * `<TileSize>` for the `<Tile>` partial.
     */
    get tileSize() {
        return tileVersionTileSizeRegistry[this.module.version];
    }
    /**
     * @inheritDoc
     */
    fetchRelatedSync() {
        const { list, overrides } = this.module.content;
        // Determine if there's a list to resolve.
        if (list === null)
            return;
        try {
            const articles = this.relatedService
                .resolveSync(list, {
                continueOrder: !this.module.content.startFromBeginning,
                overrides,
                preventDuplicates: !this.module.content.allowDuplicates,
                take: this.articlesCount
            });
            if (this.module.content.headerLink) {
                const link = this.relatedService.resolveSync(this.module.content.headerLink);
                if (link) {
                    this.headerLink = link.urlPath;
                }
            }
            this.articles = (Array.isArray(articles) ? articles : [articles]);
            this.onReady();
        }
        catch (e) {
            this.onError(e);
        }
    }
    beforeDestroy() {
        this.articles = [];
    }
    /**
     * Resolves length of articles' list displayed by the module.
     */
    static resolveArticlesCount(module, list) {
        if (!module.content.list || module.content.list.value.toString() !== list.value.toString()) {
            return 0;
        }
        return getArticlesCount(module.size, module.version);
    }
    get brandClass() {
        const { gradientColor } = this.module.content;
        if (!gradientColor) {
            return '';
        }
        // Convert domain to CSS class format
        const domain = gradientColor.toLowerCase().replace(/\./g, '-');
        return `--brand-${domain}`;
    }
};
TileModuleUi = __decorate([
    Component({
        name: 'TileModuleUi',
        components: {
            LineHeader,
            Tile,
            BootstrapLink
        },
        created() {
            this.fetchRelatedSync();
        },
        template: `
    <section
      :class="['tile-module', brandClass, moduleColoredClass, debugClass, loadingClass, sizeClass, versionClass, getThemeByBgClass]"
      :style="{ backgroundColor: customColor }"
    >
    <!-- Header -->
    <LineHeader v-if="shouldShowHeader" :site="module.content.header.site"
                :isBold="module.content.header.bold || module.content.header.isLink || false"
                :tag="lineHeaderTitleTag"
                class="tile-module__header" :theme="getThemeByBg">
      <!-- Text -->
      <template v-if="theme !== AppTheme.PremiumZone && hasHeaderText && !headerLink">
        {{ module.content.header.text }}
      </template>

      <template v-if="theme !== AppTheme.PremiumZone && hasHeaderText && headerLink">
        <BootstrapLink :link="{target: { path: headerLink }}" style="text-decoration: none">
          {{ module.content.header.text }}
        </BootstrapLink>
      </template>
    </LineHeader>

    <!-- Tiles grid -->
    <ul class="tile-module__grid list-unstyled" v-if="articles">
      <li v-for="article in articles" :key="article.urlPath"
          class="tile-module__grid__item">
        <Tile
          v-bind="{ ...tileConfig, article, isMobile, fallbackImage, windowService, isLazy: module.isLazyLoaded }" />
      </li>
    </ul>
    </section>
  `
    })
], TileModuleUi);
export { TileModuleUi };
